/* eslint-disable no-unused-vars */
import "./DiscountCodes.scss";
import { useState, useEffect } from "react";
import { InputAdornment, TextField, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { CodeColumns } from "../../SourceData";
import { ConfigProvider, DatePicker } from "antd";
import { toast } from "react-toastify";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import SearchIcon from "@mui/icons-material/Search";
import MessageModals from "../../components/MessageModals/MessageModals";
import AddGif from "./../../assets/add-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import api from "../../utils/api";
import ar_EG from "antd/es/locale/ar_EG"; // Arabic locale
import dayjs from "dayjs";
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import { usePermissions } from "./../../hooks/usePermissions";

const { RangePicker } = DatePicker;

const DiscountCodes = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [currentModalView, setCurrentModalView] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  dayjs.locale("ar");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Promo codes data state
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOpen = (view) => {
    // Check permissions based on the view
    if (view === "createDiscountCode" && !hasPermission(73)) {
      toast.error("ليس لديك صلاحية لإضافة أكواد الخصم");
      return;
    }
    if (view === "editDiscountCode" && !hasPermission(77)) {
      toast.error("ليس لديك صلاحية لتعديل أكواد الخصم");
      return;
    }

    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Reset to first page when filters change
    setPage(1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  const handleActive = async (codeId) => {
    // Check activation permission
    if (!hasPermission(75)) {
      toast.error("ليس لديك صلاحية لتفعيل/إلغاء تفعيل أكواد الخصم");
      return;
    }

    try {
      await api.patch(`/promoCodes/${codeId}/toggleState`);
      toast.success("تم تعديل الحالة بنجاح");
      fetchPromoCodes();
    } catch (error) {
      console.error("Error toggling promo code state:", error);
      toast.error("حدث خطأ أثناء تعديل الحالة");
    }
  };

  const handleDeleteClick = (code) => {
    // Check delete permission
    if (!hasPermission(76)) {
      toast.error("ليس لديك صلاحية لحذف أكواد الخصم");
      return;
    }

    setOpenDelete(true);
    setSelectedCode(code);
  };

  const handleEditClick = (code) => {
    // Check edit permission
    if (!hasPermission(77)) {
      toast.error("ليس لديك صلاحية لتعديل أكواد الخصم");
      return;
    }

    setSelectedCode(code);
    handleOpen("editDiscountCode");
  };

  // Fetch promo codes data
  const fetchPromoCodes = async () => {
    // Check view permission before fetching data
    if (!hasPermission(74)) {
      return;
    }

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.code = searchTerm;
      }

      // Add date range parameters if selected
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await api.get("/promoCodes", { params });

      if (response.data.status === "success") {
        setPromoCodes(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      toast.error("حدث خطأ أثناء جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch promo codes when dependencies change
  useEffect(() => {
    fetchPromoCodes();
  }, [page, pageSize, searchTerm, dateRange]);

  const actionColumn = [
    {
      field: "active",
      headerName: "الحالة",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        const canActivate = hasPermission(75);

        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
            disabled={!canActivate}
            style={{
              cursor: canActivate ? "pointer" : "not-allowed",
              opacity: canActivate ? 1 : 0.5,
            }}
            title={
              canActivate
                ? "تفعيل/إلغاء تفعيل الكود"
                : "ليس لديك صلاحية لتفعيل/إلغاء تفعيل أكواد الخصم"
            }
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canEdit = hasPermission(77);
        const canDelete = hasPermission(76);

        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => handleEditClick(params.row)}
              style={{
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.5,
              }}
              title={
                canEdit ? "تعديل الكود" : "ليس لديك صلاحية لتعديل أكواد الخصم"
              }
            >
              <img src={EditUser} alt="تعديل" />
            </div>
            <div
              className="cell-btn"
              onClick={() => handleDeleteClick(params.row)}
              style={{
                cursor: canDelete ? "pointer" : "not-allowed",
                opacity: canDelete ? 1 : 0.5,
              }}
              title={
                canDelete ? "حذف الكود" : "ليس لديك صلاحية لحذف أكواد الخصم"
              }
            >
              <img src={DeleteUser} alt="حذف" />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">
          اكواد الخصم <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث بالكود"
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <ConfigProvider locale={ar_EG} direction="rtl">
              <RangePicker
                format={"DD MMM"}
                style={{
                  width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                }}
                placeholder={["من", "الي"]}
                className="range-picker"
                onChange={handleDateRangeChange}
                value={dateRange}
                popupStyle={{ direction: "rtl" }}
                inputReadOnly={true}
              />
            </ConfigProvider>
          </div>

          {hasPermission(73) && (
            <BlueButton
              text="اضافة كود خصم"
              size={isMobile ? "full" : isTablet ? "fit" : "large"}
              variant="fill"
              onClick={() => handleOpen("createDiscountCode")}
              icon={<img src={AddGif} alt="" />}
            />
          )}
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={CodeColumns.concat(actionColumn)}
          rows={promoCodes.map((code, index) => ({
            ...code,
            // Generate unique id if not present
            id: code._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableRowSelectionOnClick
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "كود في الصفحة",
            },
          }}
        />
      </div>

      {openDelete && hasPermission(76) && (
        <MessageModals
          open={openDelete}
          currentView="delete"
          deleteMessage="تم حذف الكود بنجاح"
          route={`/promoCodes/${selectedCode?._id}`}
          onClose={() => {
            setOpenDelete(false);
            setSelectedCode(null);
          }}
          onDataChange={fetchPromoCodes}
        />
      )}

      {isOpen &&
        ((currentModalView === "createDiscountCode" && hasPermission(73)) ||
          (currentModalView === "editDiscountCode" && hasPermission(77))) && (
          <SwipeableModals
            open={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            currentView={currentModalView}
            selectedCode={selectedCode}
            refreshData={fetchPromoCodes}
          />
        )}
    </div>
  );
};

export default DiscountCodes;
